import { Api } from "@/core/interfaces"
import { ApiMethods } from "typegram"
import { ReactElement } from "react"

export namespace TgMailing {
  export type MailingType = {
    id: number
    name: string
  }

  /**
   * Единый формат идентификатора чата для Telegram API (Bot API и MTProto):
   *   - целое число (положительное или отрицательное, например -100…);
   *   - либо username с обязательным префиксом "@".
   * Голые строки-числа ("123") и username без "@" недопустимы — фронт
   * нормализует/отклоняет их до отправки.
   */
  export type ChatIdLike = number | `@${string}`

  /**
   * MTProto-транспорт принимает только forwardMessages / copyMessages.
   * Эти методы отсутствуют в typegram (там есть только одиночные
   * forwardMessage / copyMessage), поэтому их тело описано локально.
   */
  export type MtprotoForwardMessagesBody = {
    chat_id: ChatIdLike
    from_chat_id: ChatIdLike
    message_ids: number[]
    disable_notification?: boolean
    protect_content?: boolean
    drop_author?: boolean
  }
  export type MtprotoCopyMessagesBody = Omit<
    MtprotoForwardMessagesBody,
    "drop_author"
  >

  /**
   * Единый маппинг method → тело запроса. Bot API-методы берутся из typegram,
   * MTProto-методы (forwardMessages / copyMessages) описаны выше.
   */
  export type ApiMethodBody = {
    sendMessage: Parameters<ApiMethods<any>["sendMessage"]>[0]
    sendChatAction: Parameters<ApiMethods<any>["sendChatAction"]>[0]
    sendAnimation: Parameters<ApiMethods<any>["sendAnimation"]>[0]
    sendPhoto: Parameters<ApiMethods<any>["sendPhoto"]>[0]
    sendVideo: Parameters<ApiMethods<any>["sendVideo"]>[0]
    forwardMessages: MtprotoForwardMessagesBody
    copyMessages: MtprotoCopyMessagesBody
  }

  export type CallApiBody<M extends AvailableMethods = AvailableMethods> = {
    body: ApiMethodBody[M]
    token: string
    method: M
  }
  export type CallApiJob<M extends AvailableMethods = AvailableMethods> =
    CallApiBody<M> & {
      botId: string
    }
  export type CreateMailingData<M extends AvailableMethods = AvailableMethods> =
    {
      messages: CallApiBody<M>[]
      name: string
      scheduleDate?: Date
      comment?: string
      typeId?: MailingType["id"]
      deduplicateByUser?: boolean
      /** Target sending rate, messages per second for the whole mailing. */
      speed?: number
    }

  export type MailingState =
    | "enqueuing"
    | "running"
    | "finishing"
    | "finished"
    | "cancelled"

  export type ActiveMailing = {
    id: string
    name: string
    state: MailingState
    pending: number
    completed: number
    failed: number
    createdAt: Date
    scheduledDate?: Date
    startedDate?: Date
  }
  export type CompletedMailing = {
    id: string
    name: string
    createdAt: Date
    startedAt: Date
    finishedAt: Date
    success: number
    failed: number
    details: MailingDetails[]
    comment?: string
    type?: MailingType | null
  }
  export type MailingDetails = Omit<CallApiJob, "token"> & {
    jobName: string
    finishedOn: Date
    returnValue: ReturnType<ApiMethods<any>[keyof ApiMethods<any>]> | null
    failedReason: string | null
    attemptsMade: number
  }

  /**
   * Доступные методы рассылки.
   * Bot API: sendMessage / sendChatAction / sendAnimation / sendPhoto / sendVideo
   * MTProto: forwardMessages / copyMessages — выбор транспорта на бэк делается
   * автоматически по этим методам (см. createMailing в telegramApi).
   */
  export type AvailableMethods =
    | "sendMessage"
    | "sendChatAction"
    | "sendAnimation"
    | "sendPhoto"
    | "sendVideo"
    | "forwardMessages"
    | "copyMessages"

  export type MtprotoMethods = "forwardMessages" | "copyMessages"
  export const MTPROTO_METHODS: readonly MtprotoMethods[] = [
    "forwardMessages",
    "copyMessages",
  ] as const
  export const isMtprotoMethod = (
    method: AvailableMethods | undefined | null,
  ): method is MtprotoMethods =>
    method === "forwardMessages" || method === "copyMessages"

  export type HiddenBodyFieldProperties<
    M extends AvailableMethods,
    T extends keyof ApiMethodBody[M],
  > = {
    default?: ApiMethodBody[M][T]
    hidden: true
  }
  export type VisibleBodyFieldProperties = {
    hidden?: false
    default?: void
  }
  export type BodyFieldProperties<
    M extends AvailableMethods,
    T extends keyof ApiMethodBody[M],
  > = (HiddenBodyFieldProperties<M, T> | VisibleBodyFieldProperties) & {
    required: boolean
    name?: string
    helperText?: string | ReactElement
    validation?: Parameters<String["match"]>[0]
    parseValue?: (value: string | null | undefined) => any
    columnHeaderMatch?: Parameters<String["match"]>[0]
  }
  export type BodyFieldsDefinitions<
    M extends AvailableMethods,
    T extends keyof Omit<CallApiBody<M>["body"], "chat_id">,
  > = Record<M, Record<T, TgMailing.BodyFieldProperties<M, T>>>
  export type BodyFieldToColumnNameCollation<M extends AvailableMethods> =
    Record<keyof CallApiBody<M>["body"], string>

  export type Filters = {
    [key: string]: number | number[] | string | string[]
  }

  /** Тело init: параметры легаси минус messages, плюс выбор транспорта. */
  export type CreateMailingInitData = Omit<CreateMailingData, "messages"> & {
    useMtProto?: boolean
  }
  export type CreateMailingInitReq = Api.Request<{
    body: CreateMailingInitData
  }>
  export type CreateMailingInitRes = { id: string; state: "enqueuing" }

  /**
   * Аргумент отправки страницы. bodyJson — ЗАРАНЕЕ сериализованное тело
   * { pageIndex, messages }; отправляется как есть (байтовые ретраи §4.3 дельты).
   */
  export type SendRecipientsPageArgs = Api.Request<{
    pathParams: { id: string }
    pageIndex: number
    bodyJson: string
  }>
  export type SendRecipientsPageRes = {
    id: string
    pageIndex: number
    enqueued: number
  }

  export type CommitMailingReq = Api.Request<{
    pathParams: { id: string }
    body: { expectedTotal: number }
  }>
  export type CommitMailingRes = {
    id: string
    total: number
    state: "running" | "finishing"
  }

  export type EnqueueProgressReq = Api.Request<{ pathParams: { id: string } }>
  export type EnqueueProgressRes = {
    id: string
    state: MailingState
    enqueued: number
    total: number | null
    pagesDone: number[] | null
  }

  /** Тела 409-конфликтов (дельта §5) — discriminate union для маппинга. */
  export type PagedEnqueueConflict =
    | { conflict: "limit"; reserved: number; maxTotal: number }
    | { conflict: "closed" }
    | { conflict: "committed" }
    | { conflict: "hash_mismatch"; message: string }
    | { conflict: "aborted" }
    | { conflict: "mismatch"; enqueued: number; expectedTotal: number }

  export type ActiveMailingsReq = Api.Request
  export type ActiveMailingsRes = ActiveMailing[]

  export type FinishedMailingsReq = Api.Request
  export type FinishedMailingsRes = Omit<CompletedMailing, "details">[]

  export type MailingTypesReq = Api.Request
  export type MailingTypesRes = MailingType[]

  /**
   * Полная отмена рассылки (DELETE /telegram/remove?id=<mailingId>).
   * Останавливает запланированную и текущую рассылку, удаляет все джобы и
   * данные. Отчёт в БД не сохраняется. id передаётся query-параметром.
   */
  export type RemoveMailingReq = Api.Request<{
    queryParams: { id: ActiveMailing["id"] }
  }>
  export type RemoveMailingRes = void

  /**
   * Досрочное завершение рассылки (PATCH /telegram/finnish?id=<mailingId>).
   * Останавливает рассылку и сохраняет отчёт (статистику + детали) в БД.
   * id передаётся query-параметром.
   */
  export type FinishMailingReq = Api.Request<{
    queryParams: { id: ActiveMailing["id"] }
  }>
  export type FinishMailingRes = void
}
